import { useHistory, useLocation } from 'react-router-dom';

import { useCallback, useMemo } from 'react';
import { PathParams } from '@/store/enums';

type IReturn<T> = [param: T | null, setParam: (param: T) => void, clearParam: () => void];

function usePathParam<T extends number | string = number>(paramName: PathParams): IReturn<T> {
  const history = useHistory();
  const location = useLocation();

  const param = useMemo((): T | null => {
    const query = new URLSearchParams(location.search);
    const queryParam = query.get(paramName);
    if (!isNaN(Number(queryParam))) {
      return (Number(queryParam) ?? null) as T | null;
    }
    return (queryParam ?? null) as T | null;
  }, [location.search, paramName]);

  const setParam = useCallback(
    (id: T) => {
      const params = new URLSearchParams(history.location.search);
      params.set(paramName, id.toString());
      history.push({ search: params.toString() });
    },
    [history, paramName],
  );

  const clearParam = useCallback(() => {
    const params = new URLSearchParams(history.location.search);
    params.delete(paramName);
    history.push({ search: params.toString() });
  }, [history, paramName]);

  return [param, setParam, clearParam];
}

export { usePathParam };
